import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  ScrollView,
  StyleSheet,
  Dimensions,
  Linking,
  Platform
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from './App.web';
import * as Font from 'expo-font';
import Clipboard from '@react-native-clipboard/clipboard';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiUrl } from './utils/config';
import Toast from 'react-native-root-toast';
import ParentalGateModal from './ParentalGateModal';


const { width } = Dimensions.get('window');

type NavigationProp = StackNavigationProp<RootStackParamList, 'ReferralPage'>;

const ReferralPage: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [referralLink, setReferralLink] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [userTokens, setUserTokens] = useState(0);
  const [containerWidth, setContainerWidth] = useState(width);
  const [imageWidth, setImageWidth] = useState(Math.min(width * 0.9, 500));
  const [showParentalGate, setShowParentalGate] = useState(false);
  const [parentalGateIntent, setParentalGateIntent] = useState<'facebook' | 'instagram' | 'twitter' | 'email' | 'subscription' | null>(null);

  const getBaseUrl = (): string => {
    if (Platform.OS === 'web'){
      const host = window.location.hostname;
      if (host.includes('localhost')) return 'http://localhost:19006';
      if (host.includes('dev')) return 'https://dev.interactivebedtime.com';
      if (host.includes('test')) return 'https://test.interactivebedtime.com';
    }
    return 'https://interactivebedtime.com';
  };

  const handleCopyReferralLink = async () => {
      if (userId) {
        const baseUrl = getBaseUrl();
        const copiedReferralLink = referralLink
            ? referralLink
            : `${baseUrl}/stories?referral=${userId}`;

        Clipboard.setString(copiedReferralLink);
        setReferralCopied(true);
        setTimeout(() => setReferralCopied(false), 2000);
      }
    };

  const loadFonts = async () => {
        await Font.loadAsync({
        'Futura': require('./assets/fonts/futura.ttf'),
        });
    };

  const getAuthToken = useCallback(async (): Promise<any | null> => {
      try {
          const session = await fetchAuthSession();
          const id_token = session?.tokens?.idToken;
          return id_token || null;
      } catch (error) {
          console.error('Error retrieving auth token:', error);
          return null;
      }
      }, []);

  const getUserID = async (): Promise<string | null> => {
      try {
      const session = await fetchAuthSession();
      const user_id = session?.tokens?.accessToken?.payload?.sub;
      return user_id || null;
      } catch (error) {
      console.error('Error retrieving user ID:', error);
      return null;
      }
  };

  const fetchUserTokens = useCallback(async () => {
      try {
        const user_id = await getUserID();
        if (!user_id) throw new Error('Unable to retrieve user ID');
    
        const authToken = await getAuthToken();
        if (!authToken) throw new Error('Unable to retrieve auth token');
    
        const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${user_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        });
    
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
    
        const data = await response.json();
        setUserTokens(data.token_balance || 0);
      } catch (error) {
        console.error('Error fetching user tokens:', error);
      }
    }, [apiUrl, getAuthToken]);

  const handleEmailShare = (skipParentalGate = false) => {
    if (!skipParentalGate) {
      setParentalGateIntent('facebook');
      setShowParentalGate(true);
      return;
    }
    if (!referralLink) return;

    const subject = encodeURIComponent('Join me on Interactive Bedtime!');
    const body = encodeURIComponent(
      `Welcome to Interactive Bedtime!\n\n` +
      `A friend thinks you’ll love it — sign up using their referral link and you’ll both get 2 free tokens 🎉\n\n` +
      `${referralLink}\n\n` +
      `Already have the app? Just paste the link in the referral section.\n\n` +
      `— The Interactive Bedtime Team`
    );

    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;

    Linking.openURL(mailtoUrl).catch(err =>
      console.error('Failed to open email client:', err)
    );
  };

  const handleFacebookShare = async (skipParentalGate = false) => {
    if (!skipParentalGate) {
      setParentalGateIntent('facebook');
      setShowParentalGate(true);
      return;
    }
    const userId = await getUserID();
    if (!userId) return;

    const referralUrl = `https://share.interactivebedtime.com?referral=${userId}`;
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}`;

    Linking.openURL(facebookShareUrl).catch(err =>
      console.error('Failed to open Facebook share URL:', err)
    );
  };


  const handleInstagramShare = async (skipParentalGate = false) => {
    if (!skipParentalGate) {
      setParentalGateIntent('facebook');
      setShowParentalGate(true);
      return;
    }
    if (!referralLink) return;

    Clipboard.setString(referralLink);
    Toast.show('Referral link copied! Paste it into your Instagram story or bio.', {
      duration: Toast.durations.SHORT,
      position: Toast.positions.BOTTOM,
      backgroundColor: '#333',
      textColor: '#fff',
      shadow: true,
      animation: true,
      hideOnPress: true,
    });

    const appUrl = 'instagram://story-camera';
    const webUrl = 'https://www.instagram.com/';

    // Web: always use web URL
    if (Platform.OS === 'web') {
      Linking.openURL(webUrl);
      return;
    }

    // Mobile native
    try {
      const supported = await Linking.canOpenURL(appUrl);
      Linking.openURL(supported ? appUrl : webUrl);
    } catch {
      Linking.openURL(webUrl);
    }
  };

  const handleTwitterShare = async (skipParentalGate = false) => {
    if (!skipParentalGate) {
      setParentalGateIntent('facebook');
      setShowParentalGate(true);
      return;
    }
    const userId = await getUserID();
    if (!userId) return;

    const referralUrl = `https://legal.interactivebedtime.com/share.html?referral=${userId}`;
    const tweetText = `Check out this awesome interactive bedtime story app! Use my referral link to get 2 free tokens 🎉`;

    const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(referralUrl)}`;

    Linking.openURL(twitterShareUrl).catch(err =>
      console.error('Failed to open Twitter share URL:', err)
    );
  };

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('SettingsPage'); // Navigate to StoriesPage explicitly
    }
  };

  const handleSubscriptions = () => {
    if (Platform.OS === 'android') {
      setParentalGateIntent('subscription');
      setShowParentalGate(true);
    } else {
      navigation.navigate('SubscriptionPage');
    }
  };

  const handleParentalGateSuccess = () => {
    if (!parentalGateIntent) return;

    switch (parentalGateIntent) {
      case 'facebook':
        handleFacebookShare(true);
        break;
      case 'instagram':
        handleInstagramShare(true);
        break;
      case 'twitter':
        handleTwitterShare(true);
        break;
      case 'email':
        handleEmailShare(true);
        break;
      case 'subscription':
        navigation.navigate('SubscriptionPage');
        break;
    }

    setShowParentalGate(false);
    setParentalGateIntent(null);
  };

  const handleParentalGateCancel = () => {
    setShowParentalGate(false);
    setParentalGateIntent(null);
  };


  useEffect(() => {
      const initializeAndFetch = async () => {
        loadFonts();
          const user_id = await getUserID();
          const base_Url = getBaseUrl();
          await fetchUserTokens();
          setUserId(user_id);
          setReferralLink(`${base_Url}/stories?referral=${user_id}`);
      };
      initializeAndFetch();
    }, []);

  return (
    <View style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack}>
          <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
        </TouchableOpacity>
        <View style={styles.tokenContainer}>
          <TouchableOpacity onPress={handleSubscriptions}>
            <Image source={require('./assets/tokens.png')} style={styles.icon} />
          </TouchableOpacity>
          <Text style={styles.tokenCount}>{userTokens}</Text>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View
          onLayout={(event) => {
            const layoutWidth = event.nativeEvent.layout.width;
            setContainerWidth(layoutWidth);
            setImageWidth(Math.min(layoutWidth * 0.9, 500));
          }}
          style={{ width: '100%', alignItems: 'center' }}
        >

        <Text style={styles.title}>Give 2 tokens, get 2 tokens!</Text>

        <Image
          source={require('./assets/referral.png')}
          style={{
            width: imageWidth,
            height: imageWidth,
            marginBottom: 24,
          }}
          resizeMode="contain"
        />

        <Text style={styles.description}>
          You’ll earn 2 tokens, and they'll{'\n'}earn 2 tokens when they sign up!
        </Text>

        <Text style={styles.linkLabel}>Your Referral Link</Text>

        <View style={styles.linkRowWrapper}>
            <View style={styles.linkRowShadowBackground} />
            <View style={styles.linkRow}>
                <View style={styles.linkTextContainer}>
                <Text numberOfLines={1} style={styles.linkText}>
                    {referralLink}
                </Text>
                </View>
                <TouchableOpacity style={styles.copyButton} onPress={handleCopyReferralLink}>
                <Text style={styles.copyButtonText}>
                    {referralCopied ? 'Copied!' : 'Copy'}
                </Text>
                </TouchableOpacity>
            </View>
        </View>



        <Text style={styles.shareText}>or share link via</Text>

        <View style={styles.socialRow}>
          <TouchableOpacity
            onPress={() => {
              setParentalGateIntent('facebook');
              setShowParentalGate(true);
            }}
            disabled={!referralLink}
          >
            <Image source={require('./assets/icon-facebook.png')} style={styles.socialIcon} />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => {
              setParentalGateIntent('instagram');
              setShowParentalGate(true);
            }}
            disabled={!referralLink}
          >
            <Image source={require('./assets/icon-instagram.png')} style={styles.socialIcon} />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => {
              setParentalGateIntent('twitter');
              setShowParentalGate(true);
            }}
            disabled={!referralLink}
          >
            <Image source={require('./assets/icon-twitter.png')} style={styles.socialIcon} />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => {
              setParentalGateIntent('email');
              setShowParentalGate(true);
            }}
            disabled={!referralLink}
          >
            <Image source={require('./assets/icon-email.png')} style={styles.socialIcon} />
          </TouchableOpacity>

        </View>
        </View>
      </ScrollView>
      <ParentalGateModal
        isVisible={showParentalGate}
        onSuccess={handleParentalGateSuccess}
        onCancel={handleParentalGateCancel}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#F7F8FB',
  },
  header: {
    height: 60,
    paddingHorizontal: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  tokenContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tokenCount: {
    marginRight: 10,
    marginLeft: 5,
    color: '#534466',
    fontFamily: 'Futura',
  },
  iconButton: {
    padding: 8,
  },
  icon: {
    width: 24,
    height: 24,
  },
  content: {
    alignItems: 'center',
    padding: 24,
    paddingBottom: 60,
  },
  title: {
    fontSize: 24,
    fontFamily: 'Futura',
    fontWeight: 'bold',
    color: 'black',
    marginBottom: 16,
    textAlign: 'center',
  },
  image: {
    aspectRatio: 1,
    marginBottom: 24,
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    fontFamily: 'Futura',
    lineHeight: 20,
    color: '#333',
    marginBottom: 32,
  },
  linkLabel: {
    fontFamily: 'Futura',
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 8,
    color: '#333',
  },
  linkRowWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    width: '100%',
    maxWidth: 400,
    position: 'relative',
  },

  linkRowShadowBackground: {
      position: 'absolute',
      backgroundColor: '#AFC9BF',
      width: '100%',
      height: 55,
      borderRadius: 24,
      zIndex: 0,
      top: 4,
      left: 4,
  },

  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    borderRadius: 24,
    overflow: 'hidden', // ensures the overlap looks clean
    backgroundColor: '#544564',
    zIndex: 1,
    height: 55,
  },

  linkTextContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    paddingVertical: 17,
    paddingLeft: 17,
    borderTopLeftRadius: 24,
    borderBottomLeftRadius: 24,
    borderTopRightRadius: 40, // creates that "cut-in" effect
    borderBottomRightRadius: 40,
    marginRight: -20, // overlaps into the button
    zIndex: 2,
    height: 55,
  },

  linkText: {
    fontSize: 15,
    color: '#AEAEAE',
    fontWeight: 'bold',
    fontFamily: 'Futura',
  },

  copyButton: {
    backgroundColor: '#544564',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderTopRightRadius: 24,
    borderBottomRightRadius: 24,
    zIndex: 1,
  },

  copyButtonText: {
    color: '#fff',
    fontSize: 20,
    fontFamily: 'Futura',
    fontWeight: 'bold',
  },
  shareText: {
    fontSize: 16,
    fontFamily: 'Futura',
    marginBottom: 16,
    color: '#444',
  },
  socialRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
  },
  socialIcon: {
    width: 32,
    height: 32,
    marginHorizontal: 8,
  },
});

export default ReferralPage;
